import { useQuery } from "@tanstack/react-query";
import { config } from "@/config";
import {
  buildPermissionsRequest,
  type Permissions,
  type PermissionsResponse,
  transformPermissionsResponse,
} from "@/lib/auth/permissions";
import { fetcher } from "@/lib/react-query/fetch";

/**
 * Fetches permissions from the authorization service.
 * Uses POST method to send permissions request body.
 * Authentication errors are handled by the common fetcher.
 *
 * @param projectId - Optional project ID for project-specific permissions
 * @returns Permissions response from the authorization service
 * @throws Error if the request fails (after handling auth errors)
 */
async function fetchPermissions(projectId?: string): Promise<PermissionsResponse> {
  const authServiceUrl = config.auth.serviceUrl;
  const url = `${authServiceUrl}/api/authz/permissions`;
  const requestBody = buildPermissionsRequest(projectId);

  return fetcher<PermissionsResponse>(url, {
    method: "POST",
    data: requestBody,
  });
}

/**
 * Result type for usePermissions hook
 */
export type UsePermissionsResult = {
  /** Permissions map (action -> boolean), undefined while loading */
  permissions: Permissions | undefined;
  /** Whether the permissions are currently being fetched */
  isLoading: boolean;
  /** Error if the fetch failed */
  error: Error | null;
  /** Function to manually refetch permissions */
  refetch: () => void;
};

/**
 * Hook to fetch and cache user permissions.
 *
 * Supports both global permissions (no projectId) and project-specific permissions (with projectId).
 * Uses React Query for caching with a 5-minute stale time.
 *
 * @param projectId - Optional project ID. If provided, fetches project-specific permissions.
 *                    If omitted, fetches global proofing flow permissions.
 * @param options - Optional configuration
 * @param options.enabled - Whether to enable the query. Defaults to true. Set to false to prevent fetching.
 *
 * @returns Permissions data, loading state, error, and refetch function
 *
 * @example
 * // Global permissions (in AuthProvider or overview page)
 * const { permissions, isLoading } = usePermissions();
 * if (permissions?.access_overview) {
 *   // User can access proofing flow
 * }
 *
 * @example
 * // Project-specific permissions (in side sheet)
 * const { permissions, isLoading } = usePermissions(projectId);
 * if (permissions?.edit_metadata) {
 *   // User can edit metadata for this project
 * }
 *
 * @example
 * // Only fetch when user is authenticated
 * const { permissions } = usePermissions(undefined, { enabled: !!user });
 */
export function usePermissions(
  projectId?: string,
  options?: { enabled?: boolean },
): UsePermissionsResult {
  // Build cache key based on whether we have a projectId
  const queryKey = projectId ? ["permissions", "project", projectId] : ["permissions", "global"];

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await fetchPermissions(projectId);
      return transformPermissionsResponse(response);
    },
    enabled: options?.enabled ?? true, // Allow disabling the query
    staleTime: 5 * 60 * 1000, // 5 minutes - data is considered fresh
    gcTime: 10 * 60 * 1000, // 10 minutes - cache time (renamed from cacheTime in v5)
    retry: 1, // Retry failed requests once
    refetchOnWindowFocus: false, // Don't refetch on tab focus
  });

  return {
    permissions: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
